import { useCallback, useEffect, useRef, useState } from 'react'

import { useGetUserQuery } from 'common'
import {
  useFetchSchedulingAssistantSettingsQuery,
  usePostSchedulingAssistantSettingMutation,
} from 'hooks/scheduling_assistant/api'
import { getSchedulingAssistantSettingValueByName } from 'hooks/scheduling_assistant/helpers'

import { AI_CHAT_DISCOVERY_INTRO_MODAL_DISMISSED_SETTING_NAME } from '../constants'

const DISMISSED_SETTING_TYPE = 'BOOLEAN'
const DISMISSED_VALUE = 'true'
const NOT_DISMISSED_VALUE = 'false'

/** Single dismiss lifecycle — avoids parallel isDismissed + isLoading booleans. */
export const INTRO_MODAL_DISMISS_STATUS = Object.freeze({
  PENDING: 'pending',
  SHOW: 'show',
  DISMISSED: 'dismissed',
})

/**
 * One-time resolve of dismiss status from settings (or clears it when reset is requested).
 * Kept file-private so the public hook stays focused on the dismiss API.
 */
const useResolveIntroModalDismissStatus = ({
  userUuid,
  isSettingsLoading,
  isSettingsError,
  settings,
  resetRequested,
  persistDismissed,
  setStatus,
}) => {
  const hasResolvedRef = useRef(false)

  useEffect(() => {
    if (!userUuid || isSettingsLoading || hasResolvedRef.current) return

    if (resetRequested) {
      hasResolvedRef.current = true
      // Always surface SHOW so ?reset=1 can retest even if the clear mutation fails
      // (same recovery pattern as useCallieOnboardingState).
      persistDismissed(false)
        .finally(() => {
          setStatus(INTRO_MODAL_DISMISS_STATUS.SHOW)
        })
        .catch(() => {})
      return
    }

    // Stay PENDING on a failed/empty fetch so an outage cannot re-prompt dismissed users
    // (undefined `data` would otherwise look like a missing setting → SHOW).
    if (isSettingsError || !Array.isArray(settings)) return

    hasResolvedRef.current = true
    const rawValue = getSchedulingAssistantSettingValueByName(
      settings,
      AI_CHAT_DISCOVERY_INTRO_MODAL_DISMISSED_SETTING_NAME,
    )
    setStatus(
      rawValue === DISMISSED_VALUE
        ? INTRO_MODAL_DISMISS_STATUS.DISMISSED
        : INTRO_MODAL_DISMISS_STATUS.SHOW,
    )
  }, [
    userUuid,
    isSettingsLoading,
    isSettingsError,
    settings,
    resetRequested,
    persistDismissed,
    setStatus,
  ])
}

/**
 * Persists discovery intro modal dismiss in the `ai_exec_assistant_*` settings store.
 * `resetRequested` (wired to `?reset=1`) clears the flag on mount for retesting.
 * `shouldDismiss` avoids user/settings network requests when the intro gate is not eligible.
 *
 * @param {{ resetRequested?: boolean, shouldDismiss?: boolean }} [options]
 * @returns {{
 *   status: typeof INTRO_MODAL_DISMISS_STATUS[keyof typeof INTRO_MODAL_DISMISS_STATUS],
 *   dismiss: () => Promise<void>,
 * }}
 */
export const useAiChatDiscoveryIntroModalDismiss = ({
  resetRequested = false,
  shouldDismiss = false,
} = {}) => {
  const { data: user } = useGetUserQuery(undefined, { skip: shouldDismiss })
  const userUuid = user?.uuid

  const {
    data: settings,
    isLoading: isSettingsLoading,
    isError: isSettingsError,
  } = useFetchSchedulingAssistantSettingsQuery(userUuid, {
    skip: shouldDismiss || !userUuid,
  })
  const [postSetting] = usePostSchedulingAssistantSettingMutation()
  const [status, setStatus] = useState(INTRO_MODAL_DISMISS_STATUS.PENDING)

  const persistDismissed = useCallback(async (dismissed) => {
    await postSetting({
      userUuid,
      settingName: AI_CHAT_DISCOVERY_INTRO_MODAL_DISMISSED_SETTING_NAME,
      settingValue: dismissed ? DISMISSED_VALUE : NOT_DISMISSED_VALUE,
      settingType: DISMISSED_SETTING_TYPE,
    }).unwrap()
    setStatus(
      dismissed
        ? INTRO_MODAL_DISMISS_STATUS.DISMISSED
        : INTRO_MODAL_DISMISS_STATUS.SHOW,
    )
  }, [userUuid, postSetting])

  const dismiss = useCallback(async () => {
    await persistDismissed(true)
  }, [persistDismissed])

  useResolveIntroModalDismissStatus({
    userUuid,
    isSettingsLoading,
    isSettingsError,
    settings,
    resetRequested,
    persistDismissed,
    setStatus,
  })

  return {
    status,
    dismiss,
  }
}
